import React, { useMemo, useState } from 'react';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import FinDropdown from '../../connect/finance/fin-dropdown';
import { dash, platformCell, zonedDateTimeCell } from './finance-cells';
import OrderReportPager from './order-report-pager';

const PAGE_SIZE = 22;
const ALL_STORES = '';

// Frame 2c's Transactions, revised: six columns, not the mock's thirteen —
// the owner chose the old Refunds tab's set (Type, Order #, Order ID, Date,
// Net amount) plus Platform, because fees per order are not the question on
// a payout page and the order number and id are what identify the order.
// The per-order fee detail lives on Order Reports.
//
// One table, no tabs (owner, 2026-09-23): charges, refunds and adjustments
// share these columns and the Type column tells them apart. They follow the
// export's own order — charges, then refunds, then adjustments — so the
// screen and the file read the same way.
//
// Adjustments have no order and no store — they are Stripe balance
// transactions of their own — so the Stripe id stands in for the order
// number, and Order ID and Platform both read as a dash (Review Focus 3),
// though the adjustment still carries its own date.
// Owner, 2026-09-25: Platform after the order, and the date in the store's
// own time.
// Below `sm` Platform, Order ID and Date are not drawn (owner, 2026-09-22:
// on a phone Date and Net amount fell behind a sideways scroll): Type ·
// Order # · Net.
const GRID = 'grid grid-cols-[80px_minmax(0,1fr)_100px] sm:grid-cols-[110px_110px_96px_1fr_160px_120px] gap-x-3 px-6';
const HEADERS = ['Type', 'Order #', 'Platform', 'Order ID', 'Date', 'Net amount'];
const PHONE_HIDDEN = new Set(['Platform', 'Order ID', 'Date']);
const PHONE_HIDDEN_CLS = 'hidden sm:block';

// The word a reader sees for a row, and the only place that decides it.
// Stripe's own classification first (`category`, API 2026-10-07 — the same
// names the Summary card's lines use: a dispute is a "Dispute", not an
// "Adjustment"), with two kinds named more precisely than their category
// (a `payout_cancel` is also a `payout_reversal`), then the tab
// (`type`: 'charge' / 'refund' / 'adjustment', and 'payout' on the
// Upcoming page) for an older API that sends neither.
const TYPE_LABELS = { charge: 'Charge', refund: 'Refund', adjustment: 'Adjustment', payout: 'Payout' };
const KIND_LABELS = { payout_failure: 'Payout returned', payout_cancel: 'Payout canceled' };
const CATEGORY_LABELS = {
  charge: 'Charge',
  refund: 'Refund',
  refund_failure: 'Refund failed',
  partial_capture_reversal: 'Refund',
  dispute: 'Dispute',
  dispute_reversal: 'Dispute reversal',
  payout_reversal: 'Payout returned',
  payout: 'Payout',
  fee: 'Stripe fee',
  transfer: 'Transfer',
  transfer_reversal: 'Transfer reversed',
  topup: 'Top-up',
  topup_reversal: 'Top-up reversed',
};
export const typeLabel = (row) =>
  KIND_LABELS[row.kind] || CATEGORY_LABELS[row.category] || TYPE_LABELS[row.type] || row.type || dash();

// A deduction reads as one: parentheses, in the same ink as every other
// amount. Not red and not greyed out (owner, 2026-09-23): a refund is an
// ordinary part of a payout, not an alarm.
const amountCell = (value) => (
  <span className="font-bold tabular-nums text-fin-ink">
    {value < 0 ? `(${formatCurrency(Math.abs(value))})` : formatCurrency(value || 0)}
  </span>
);

const PayoutTransactions = ({ detail }) => {
  const [storeId, setStoreId] = useState(ALL_STORES);
  const [page, setPage] = useState(1);

  // The Upcoming page's manual payouts come last: they are what left, after
  // what came in (owner, 2026-10-07: the list must add up to the summary).
  const all = useMemo(
    () => [
      ...(detail?.charges || []),
      ...(detail?.refunds || []),
      ...(detail?.adjustments || []),
      ...(detail?.payouts || []),
    ],
    [detail],
  );

  // Only the stores this payout actually touched: a filter offering a store
  // with nothing in it is a dead end dressed as a choice.
  const storeOptions = useMemo(() => {
    const seen = new Map();
    all.forEach((row) => {
      if (row.storeId && row.storeName && !seen.has(row.storeId)) seen.set(row.storeId, row.storeName);
    });
    return [
      { value: ALL_STORES, label: 'All stores' },
      ...[...seen.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [all]);

  const rows = useMemo(
    () => (storeId ? all.filter((row) => row.storeId === storeId) : all),
    [all, storeId],
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Clamped: switching store can shrink the list out from under a page
  // number the reader was already on.
  const current = Math.min(page, pageCount);
  const shown = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const first = rows.length === 0 ? 0 : (current - 1) * PAGE_SIZE + 1;
  const last = Math.min(current * PAGE_SIZE, rows.length);

  return (
    <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 pb-3.5 pt-[18px]">
        <h2 className="text-[15px] font-bold tracking-[-0.01em] text-fin-ink">Transactions</h2>
        <FinDropdown
          mode="radio"
          aria-label="Store"
          value={storeId}
          options={storeOptions}
          onChange={(next) => {
            setStoreId(next);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-x-auto">
        <div className="sm:min-w-[780px]">
          <div role="table" aria-label="Payout transactions">
            <div role="row" className={cn(GRID, 'border-y border-fin-border py-3.5 text-[13.5px] font-bold text-fin-ink')}>
              {HEADERS.map((h) => (
                <div
                  key={h}
                  role="columnheader"
                  className={cn(h === 'Net amount' && 'text-right', PHONE_HIDDEN.has(h) && PHONE_HIDDEN_CLS) || undefined}
                >
                  {h}
                </div>
              ))}
            </div>
            <div role="rowgroup" className={shown.length > 0 ? 'min-h-[320px]' : undefined}>
              {shown.map((row, index) => (
                <div
                  key={row.stripeId || `${row.orderNumber}-${index}`}
                  role="row"
                  className={cn(GRID, 'items-center py-3 text-[13px]', index < shown.length - 1 && 'border-b border-fin-hair')}
                >
                  <div role="cell" className="truncate font-medium text-fin-muted-2">{typeLabel(row)}</div>
                  <div role="cell" className="truncate font-semibold tabular-nums text-fin-ink">
                    {row.orderNumber ? `#${row.orderNumber}` : row.stripeId || dash()}
                  </div>
                  <div role="cell" className={PHONE_HIDDEN_CLS}>{platformCell(row.platform)}</div>
                  <div
                    role="cell"
                    className={cn('truncate font-mono text-[12px] text-fin-muted', PHONE_HIDDEN_CLS)}
                    title={row.orderId || undefined}
                  >
                    {row.orderId || dash()}
                  </div>
                  <div role="cell" className={PHONE_HIDDEN_CLS}>{zonedDateTimeCell(row.date, row.timezoneId)}</div>
                  <div role="cell" className="text-right">{amountCell(row.amount)}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Sibling of role=table, never a child (round-1 ARIA ruling). */}
          {shown.length === 0 ? (
            <div className="flex min-h-[320px] items-center justify-center px-6 text-center">
              <p className="text-[13px] text-fin-muted">No transactions in this payout.</p>
            </div>
          ) : null}
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
          <p className="text-[13px] text-fin-muted">{`Showing ${first}–${last} of ${rows.length}`}</p>
          <OrderReportPager page={current} pageCount={pageCount} onChange={setPage} />
        </div>
      ) : null}

      {/* Refunds appear here as soon as Stripe reports them, but the bank
          rail still takes its own time — the same caveat the old Refunds tab
          carried. */}
      <p className="px-6 pb-4 text-[12.5px] text-fin-faint">
        Funds typically become available within ~2 business days.
      </p>
    </section>
  );
};

export default PayoutTransactions;
